import React, { useEffect, useState } from "react";
import {
  getProviderSubscription,
  createSubscriptionSession,
  cancelProviderSubscription,
} from "../APIs/providerApi";
import { showToast } from "../APIs/utils/toastManager";

export default function BillingTab({ providerId, refreshKey = 0 }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    getProviderSubscription(providerId)
      .then(setSub)
      .catch(() => setSub(null))
      .finally(() => setLoading(false));
  }, [providerId, refreshKey]);

  const handleSubscribe = async () => {
    try {
      setRedirecting(true);
      const { checkoutUrl } = await createSubscriptionSession();
      window.location.href = checkoutUrl;
    } catch (err) {
      showToast("Failed to start checkout. Please try again.", "error");
      setRedirecting(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = window.confirm(
      "Cancel your MedMaze provider subscription? This will deactivate premium access immediately.",
    );
    if (!confirmed) return;

    try {
      setCancelling(true);
      const updated = await cancelProviderSubscription(providerId);
      setSub(updated);
      showToast("Subscription cancelled.", "success");
    } catch (err) {
      showToast(
        err?.response?.data?.message ||
          "Failed to cancel subscription. Please try again.",
        "error",
      );
    } finally {
      setCancelling(false);
    }
  };

  if (loading)
    return <p className="text-gray-500 text-sm">Loading billing info...</p>;

  const status = sub?.status ?? "UNKNOWN";
  const trialEnd = sub?.trialEnd ? new Date(sub.trialEnd) : null;
  const currentPeriodEnd = sub?.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd)
    : null;
  const accessUntil = currentPeriodEnd || trialEnd;
  const hasRetainedAccess = accessUntil ? accessUntil > new Date() : false;
  const daysLeft = trialEnd
    ? Math.max(0, Math.ceil((trialEnd - Date.now()) / 86400000))
    : null;

  const isCancelledRetained = status === "CANCELLED" && hasRetainedAccess;
  const isExpired =
    status === "EXPIRED" || (status === "CANCELLED" && !hasRetainedAccess);
  const isTrial = status === "TRIAL";
  const isActive = status === "ACTIVE";

  return (
    <div className="max-w-lg space-y-6">
      <h2 className="text-lg font-semibold text-gray-800">
        Billing & Subscription
      </h2>

      {isExpired && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium text-sm">
            Your access has expired. Subscribe to restore full access.
          </p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Plan</span>
          <span className="text-sm font-medium text-gray-800">
            MedMaze Provider — $50/month
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Status</span>
          <span
            className={`text-sm font-semibold px-2 py-0.5 rounded-full ${
              isActive
                ? "bg-green-100 text-green-700"
                : isTrial
                  ? "bg-teal-100 text-teal-700"
                  : isCancelledRetained
                    ? "bg-amber-100 text-amber-700"
                    : "bg-red-100 text-red-700"
            }`}
          >
            {isCancelledRetained ? "CANCELLED - ACCESS ACTIVE" : status}
          </span>
        </div>
        {isTrial && daysLeft !== null && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Trial ends</span>
            <span className="text-sm font-medium text-gray-700">
              {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining
            </span>
          </div>
        )}
        {isCancelledRetained && accessUntil && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Access ends</span>
            <span className="text-sm font-medium text-gray-700">
              {accessUntil.toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      {!isActive && !isCancelledRetained && (
        <button
          onClick={handleSubscribe}
          disabled={redirecting}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium py-2.5 rounded-lg transition disabled:opacity-50"
        >
          {redirecting ? "Redirecting..." : "Subscribe for $50/month"}
        </button>
      )}

      {isActive && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={cancelling}
            className="w-full border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium py-2.5 rounded-lg transition disabled:opacity-50"
          >
            {cancelling ? "Cancelling..." : "Cancel Subscription"}
          </button>
          <p className="text-sm text-gray-500">
            Cancelling keeps premium access active until the end of your current
            paid period.
          </p>
        </div>
      )}
    </div>
  );
}
